import React, { useState, useEffect, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { debounce } from 'lodash'

import { API, API_ROUTER } from '../../../api'
import { ArrowButton, RangePicker, Preloader } from '../../../components/UI'
import { Select } from '../../../components/UI'
import { GENDER_OPTIONS, PROGAMER_OPTIONS } from '../config'
import { transformArrayToOptions, transformObjectToArray } from '../utils'
import Styled from '../style'

const Filters = ({ filters, onFiltersChange, onClearFilters }) => {
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    regions: [],
    cities: [],
    languages: [],
    genders: GENDER_OPTIONS,
    progamerOptions: PROGAMER_OPTIONS,
  })

  const [filtersLoading, setFiltersLoading] = useState(false)
  const [localAgeValues, setLocalAgeValues] = useState([
    filters.ageMin,
    filters.ageMax,
  ])

  useEffect(() => {
    setLocalAgeValues([filters.ageMin, filters.ageMax])
  }, [filters.ageMin, filters.ageMax])

  const debouncedAgeUpdate = useCallback(
    debounce((ageMin, ageMax) => {
      onFiltersChange(prevFilters => ({
        ...prevFilters,
        ageMin,
        ageMax,
      }))
    }, 300),
    [onFiltersChange]
  )

  const handleAgeChange = newAgeValues => {
    setLocalAgeValues(newAgeValues)
    debouncedAgeUpdate(newAgeValues[0], newAgeValues[1])
  }

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value }

    // Handle cascading filters
    if (key === 'country') {
      newFilters.region = []
      newFilters.city = []
      // Load regions for selected country
      if (value && value.length > 0) {
        const countryCode = value[0].value
        loadRegions(countryCode)
      } else {
        setFilterOptions(prev => ({ ...prev, regions: [], cities: [] }))
      }
    } else if (key === 'region') {
      newFilters.city = []
      // Load cities for selected region
      if (
        value &&
        value.length > 0 &&
        filters.country &&
        filters.country.length > 0
      ) {
        const regionCode = value[0].value
        const countryCode = filters.country[0].value
        loadCities(countryCode, regionCode)
      } else {
        setFilterOptions(prev => ({ ...prev, cities: [] }))
      }
    }

    onFiltersChange(newFilters)
  }

  const loadCountries = async () => {
    try {
      const response = await API.request({
        ...API_ROUTER.public.getCountriesList,
      })

      const countriesList = transformObjectToArray(response)
      setFilterOptions(prev => ({
        ...prev,
        countries: [
          { value: '', label: 'Select option', disabled: true },
          ...countriesList,
        ],
      }))
    } catch (err) {
      console.error('Error loading countries:', err)
    }
  }

  const loadRegions = async countryCode => {
    try {
      const response = await API.request({
        ...API_ROUTER.public.getCountryRegions,
        pathKeys: { country: countryCode },
      })

      let regionsList

      if (Array.isArray(response)) {
        regionsList = transformArrayToOptions(response)
      } else if (response.regions && Array.isArray(response.regions)) {
        regionsList = transformArrayToOptions(response.regions)
      } else {
        regionsList = transformObjectToArray(response)
      }

      setFilterOptions(prev => ({
        ...prev,
        regions: [
          { value: '', label: 'Select option', disabled: true },
          ...regionsList,
        ],
      }))
    } catch (err) {
      console.error('Error loading regions:', err)
    }
  }

  const loadCities = async (countryCode, regionCode) => {
    try {
      const response = await API.request({
        ...API_ROUTER.public.getCitiesByRegion,
        pathKeys: { country: countryCode, region: regionCode },
      })

      let citiesList

      if (Array.isArray(response)) {
        citiesList = transformArrayToOptions(response)
      } else if (response.cities && Array.isArray(response.cities)) {
        // Handle object with cities array
        citiesList = transformArrayToOptions(response.cities)
      } else {
        // If it's an object, use the object transformation
        citiesList = transformObjectToArray(response)
      }

      setFilterOptions(prev => ({
        ...prev,
        cities: [
          { value: '', label: 'Select option', disabled: true },
          ...citiesList,
        ],
      }))
    } catch (err) {
      console.error('Error loading cities:', err)
    }
  }

  const loadLanguages = async () => {
    try {
      const response = await API.request({
        ...API_ROUTER.public.getLanguages,
      })

      const languagesList = transformObjectToArray(response)
      setFilterOptions(prev => ({
        ...prev,
        languages: [
          { value: '', label: 'Select option', disabled: true },
          ...languagesList,
        ],
      }))
    } catch (err) {
      console.error('Error loading languages:', err)
    }
  }

  useEffect(() => {
    const loadAllFilterData = async () => {
      setFiltersLoading(true)
      try {
        await Promise.allSettled([loadCountries(), loadLanguages()])
      } catch (error) {
        console.error('Error loading filter data:', error)
      } finally {
        setFiltersLoading(false)
      }
    }

    loadAllFilterData()
  }, [])

  if (filtersLoading) {
    return <Preloader />
  }

  return (
    <Styled.StyledContentBox>
      <Styled.FilterTitle>
        <FormattedMessage id='ratings.addFilters' tagName='span' />
      </Styled.FilterTitle>

      <Styled.FilterBox>
        <div className='filter__raiting-title'>
          <FormattedMessage id='ratings.geo' />
        </div>
        <div className='form-group'>
          <FormattedMessage id='global.forms.labels.country' tagName='label' />
          <Select
            key='country-select'
            clearable
            value={filters.country}
            onChange={value => updateFilter('country', value)}
            options={filterOptions.countries}
            values={filterOptions.countries}
            placeholder='Select Country'
          />
        </div>
        <div className='form-group'>
          <FormattedMessage id='global.forms.labels.region' tagName='label' />
          <Select
            key='region-select'
            clearable
            value={filters.region}
            onChange={value => updateFilter('region', value)}
            options={filterOptions.regions}
            values={filterOptions.regions}
            placeholder='Select Region'
            disabled={!filters.country || filters.country.length === 0}
          />
        </div>
        <div className='form-group'>
          <FormattedMessage id='global.forms.labels.city' tagName='label' />
          <Select
            key='city-select'
            clearable
            value={filters.city}
            onChange={value => updateFilter('city', value)}
            options={filterOptions.cities}
            values={filterOptions.cities}
            placeholder='Select City'
            disabled={!filters.region || filters.region.length === 0}
          />
        </div>
      </Styled.FilterBox>

      <Styled.FilterBox>
        <div className='filter__raiting-title'>
          <FormattedMessage id='ratings.demography' />
        </div>
        <div className='form-group'>
          <label>
            <FormattedMessage id='global.forms.labels.age' />:{' '}
            <Styled.AgeDisplay>
              {localAgeValues[0]} - {localAgeValues[1]} years
            </Styled.AgeDisplay>
          </label>
          <RangePicker
            min={0}
            max={100}
            step={1}
            value={localAgeValues}
            onChange={handleAgeChange}
          />
        </div>
        <div className='form-group'>
          <FormattedMessage id='global.forms.labels.language' tagName='label' />
          <Select
            key='language-select'
            clearable
            value={filters.language}
            onChange={value => updateFilter('language', value)}
            options={filterOptions.languages}
            values={filterOptions.languages}
            placeholder='Select Language'
          />
        </div>
        <div className='form-group'>
          <FormattedMessage id='global.forms.labels.gender' tagName='label' />
          <Select
            key='gender-select'
            clearable
            value={filters.gender}
            onChange={value => updateFilter('gender', value)}
            options={filterOptions.genders}
            values={filterOptions.genders}
            placeholder='Select Gender'
          />
        </div>
      </Styled.FilterBox>

      <Styled.FilterBox>
        <div className='filter__raiting-title'>
          <FormattedMessage id='ratings.type' />
        </div>
        <div className='form-group'>
          <FormattedMessage id='global.forms.labels.progamer' tagName='label' />
          <Select
            key='progamer-select'
            clearable
            value={filters.progamer}
            onChange={value => updateFilter('progamer', value)}
            options={filterOptions.progamerOptions}
            values={filterOptions.progamerOptions}
            placeholder='Select Pro-gamer Status'
          />
        </div>
      </Styled.FilterBox>

      <Styled.FilterBox>
        <ArrowButton
          label={<FormattedMessage id='ratings.clearFilters' />}
          variant='secondary'
          action={onClearFilters}
        />
      </Styled.FilterBox>
    </Styled.StyledContentBox>
  )
}

export default Filters
